
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { 
  FileText, 
  Download, 
  Calendar, 
  CreditCard, 
  Bell, 
  BarChart2, 
  Tag,
  Calendar as CalendarIcon
} from "lucide-react";
import { 
  AngkasaMember, 
  AngkasaPromotion, 
  getAllMembers, 
  getAllPromotions 
} from "@/lib/db/angkasaMemberDb";

const MembershipReportsTab = () => {
  const [members, setMembers] = useState<AngkasaMember[]>([]);
  const [promotions, setPromotions] = useState<AngkasaPromotion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportType, setReportType] = useState("membership-overview");
  const [coopFilter, setCoopFilter] = useState("all");
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const membersData = await getAllMembers();
        setMembers(membersData);
        
        const promotionsData = await getAllPromotions();
        setPromotions(promotionsData);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch data for reports", error);
        toast({
          variant: "destructive",
          title: "Fetch Error",
          description: "Failed to load data for reports.",
        });
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [toast]);

  // Filter data based on cooperative filter
  const filteredMembers = coopFilter !== "all" 
    ? members.filter(member => member.cooperativeName === coopFilter)
    : members;

  // Generate data for membership by cooperative chart
  const membersByCoopData = () => {
    const coopCounts: {[key: string]: number} = {};
    members.forEach(member => {
      if (coopCounts[member.cooperativeName]) {
        coopCounts[member.cooperativeName] += 1;
      } else {
        coopCounts[member.cooperativeName] = 1;
      }
    });
    
    return Object.keys(coopCounts).map(coop => ({
      name: coop,
      value: coopCounts[coop]
    }));
  };

  // Generate data for membership by type chart
  const membersByTypeData = () => {
    const typeCounts: {[key: string]: number} = {};
    filteredMembers.forEach(member => {
      if (typeCounts[member.memberType]) {
        typeCounts[member.memberType] += 1;
      } else {
        typeCounts[member.memberType] = 1;
      }
    });
    
    return Object.keys(typeCounts).map(type => ({
      name: type,
      value: typeCounts[type]
    }));
  };

  // Generate data for membership by promotion group chart
  const membersByPromoGroupData = () => {
    const groupCounts: {[key: string]: number} = {};
    filteredMembers.forEach(member => {
      if (groupCounts[member.promotionGroup]) {
        groupCounts[member.promotionGroup] += 1;
      } else {
        groupCounts[member.promotionGroup] = 1;
      }
    });
    
    return Object.keys(groupCounts).map(group => ({
      name: group,
      value: groupCounts[group]
    }));
  };

  // Calculate membership metrics
  const calculateMembershipMetrics = () => {
    // Total members
    const totalMembers = filteredMembers.length;
    
    // Active vs Expired members
    const today = new Date().toISOString().split('T')[0];
    const activeMembers = filteredMembers.filter(member => member.validUntil >= today).length;
    const expiredMembers = totalMembers - activeMembers;
    
    // Members expiring in next 30 days
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
    const thirtyDaysLaterStr = thirtyDaysLater.toISOString().split('T')[0];
    const expiringMembers = filteredMembers.filter(
      member => member.validUntil >= today && member.validUntil <= thirtyDaysLaterStr
    ).length;
    
    // Total spending
    const totalSpent = filteredMembers.reduce((sum, member) => sum + member.totalSpent, 0);
    
    // Average spending per member
    const avgSpend = totalMembers > 0 ? (totalSpent / totalMembers) : 0;
    
    return {
      totalMembers,
      activeMembers,
      expiredMembers,
      expiringMembers,
      totalSpent,
      avgSpend
    };
  };

  // Calculate promotion metrics
  const calculatePromotionMetrics = () => {
    // Filter promotions by cooperative if needed
    const filteredPromotions = coopFilter !== "all"
      ? promotions.filter(promo => promo.cooperativeName === coopFilter || promo.cooperativeName === null)
      : promotions;
    
    // Current active promotions
    const today = new Date().toISOString().split('T')[0];
    const activePromotions = filteredPromotions.filter(
      promo => promo.startDate <= today && promo.endDate >= today
    ).length;
    
    // Total redemptions
    const totalRedemptions = filteredPromotions.reduce((sum, promo) => sum + promo.currentRedemptions, 0);
    
    // Redemption rate
    const maxPossibleRedemptions = filteredPromotions.reduce((sum, promo) => sum + promo.maxRedemptions, 0);
    const redemptionRate = maxPossibleRedemptions > 0 ? (totalRedemptions / maxPossibleRedemptions * 100) : 0;
    
    // Average discount value
    const avgDiscountValue = filteredPromotions.length > 0 
      ? filteredPromotions.reduce((sum, promo) => sum + promo.discountValue, 0) / filteredPromotions.length
      : 0;
    
    return {
      totalPromotions: filteredPromotions.length,
      activePromotions,
      totalRedemptions,
      redemptionRate,
      avgDiscountValue
    };
  };

  // Prepare expiring memberships report
  const expiringMemberships = () => {
    const today = new Date().toISOString().split('T')[0];
    const ninetyDaysLater = new Date();
    ninetyDaysLater.setDate(ninetyDaysLater.getDate() + 90);
    const ninetyDaysLaterStr = ninetyDaysLater.toISOString().split('T')[0];
    
    return filteredMembers
      .filter(member => member.validUntil >= today && member.validUntil <= ninetyDaysLaterStr)
      .sort((a, b) => new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime())
      .slice(0, 5);
  };

  // Prepare top spenders report
  const topSpenders = () => {
    return [...filteredMembers]
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5);
  };

  const generateReport = (reportType: string) => {
    toast({
      title: "Report Generation",
      description: `${reportType} report will be generated and downloaded soon.`,
    });
  };

  const metrics = calculateMembershipMetrics();
  const promoMetrics = calculatePromotionMetrics();
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (isLoading) {
    return <LayoutLoader message="Loading report data..." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
        <div className="flex-1">
          <Tabs defaultValue="membership" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="membership">Membership Reports</TabsTrigger>
              <TabsTrigger value="promotions">Promotion Reports</TabsTrigger>
            </TabsList>
            
            <TabsContent value="membership" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Membership Overview</h3>
                <Select value={coopFilter} onValueChange={setCoopFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by Cooperative" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cooperatives</SelectItem>
                    <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
                    <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
                    <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.totalMembers}</div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>Active: {metrics.activeMembers}</span>
                      <span>Expired: {metrics.expiredMembers}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
                    <Bell className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.expiringMembers}</div>
                    <p className="text-xs text-muted-foreground">
                      Memberships expiring in next 30 days
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Average Spend</CardTitle>
                    <Tag className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">RM {metrics.avgSpend.toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">
                      Total: RM {metrics.totalSpent.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-md">Members by Cooperative</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    {membersByCoopData().length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={membersByCoopData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {membersByCoopData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value} members`, 'Count']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-muted-foreground">No data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-md">Members by Type</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    {membersByTypeData().length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={membersByTypeData()}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => [`${value} members`, 'Count']} />
                          <Legend />
                          <Bar dataKey="value" name="Members" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-muted-foreground">No data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-md">Members by Promotion Group</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    {membersByPromoGroupData().length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={membersByPromoGroupData()}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="name" />
                          <Tooltip formatter={(value) => [`${value} members`, 'Count']} />
                          <Legend />
                          <Bar dataKey="value" name="Members" fill="#82ca9d" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-muted-foreground">No data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-md">Expiring Memberships</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Members expiring in the next 90 days
                    </p>
                  </CardHeader>
                  <CardContent>
                    {expiringMemberships().length > 0 ? (
                      <div className="space-y-4">
                        {expiringMemberships().map(member => (
                          <div key={member.memberId} className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-sm text-muted-foreground">{member.memberId} - {member.cooperativeName}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline" className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {member.validUntil}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        <Button variant="outline" className="w-full">View All Expiring</Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground">No expiring memberships</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-md">Top Spenders</CardTitle>
                </CardHeader>
                <CardContent>
                  {topSpenders().length > 0 ? (
                    <div className="space-y-4">
                      {topSpenders().map(member => (
                        <div key={member.memberId} className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-sm text-muted-foreground">{member.memberId} - {member.cooperativeName}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">RM {member.totalSpent.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Last: {member.lastPurchase || "N/A"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-muted-foreground">No spending data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <div className="flex justify-end gap-2">
                <Button onClick={() => generateReport("Membership Overview")}>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </Button>
                <Button variant="outline" onClick={() => generateReport("Membership CSV Export")}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="promotions" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Promotion Performance</h3>
                <Select value={coopFilter} onValueChange={setCoopFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by Cooperative" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cooperatives</SelectItem>
                    <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
                    <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
                    <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Promotions</CardTitle>
                    <Tag className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{promoMetrics.totalPromotions}</div>
                    <p className="text-xs text-muted-foreground">
                      Active: {promoMetrics.activePromotions}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Redemptions</CardTitle>
                    <Tag className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{promoMetrics.totalRedemptions}</div>
                    <p className="text-xs text-muted-foreground">
                      Redemption Rate: {promoMetrics.redemptionRate.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg. Discount</CardTitle>
                    <BarChart2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{promoMetrics.avgDiscountValue.toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">
                      Average discount value
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-md">Promotion Performance</CardTitle>
                </CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={promotions.map(promo => ({
                        name: promo.name,
                        current: promo.currentRedemptions,
                        max: promo.maxRedemptions,
                        rate: (promo.currentRedemptions / promo.maxRedemptions * 100).toFixed(1)
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="current" name="Current Redemptions" fill="#8884d8" />
                      <Bar dataKey="max" name="Maximum Allowable" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              
              <div className="flex justify-end gap-2">
                <Button onClick={() => generateReport("Promotion Performance")}>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </Button>
                <Button variant="outline" onClick={() => generateReport("Promotion CSV Export")}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MembershipReportsTab;
